import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportService } from '../../services/reportService';
import { Download, AlertTriangle } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const TrustBalanceReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getTrustBalances();
      setData(response.data);
    } catch (err) {
      console.error('Error fetching trust balances:', err);
      setError('Failed to load Trust Balances report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { sortConfig, requestSort, sortItems } = useSort('current_balance', 'desc');

  const handleExportCSV = () => {
    reportService.downloadCSV(reportService.getTrustBalances, {}, 'trust_balances_report.csv');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-lg" />)}
        </div>
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Trust Account Balances</h2>
        <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Trust Account Balances</p>
          <p className="text-lg font-bold text-green-700">{formatCurrency(summary.total_trust_balance || 0)}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Credits</p>
          <p className="text-lg font-bold text-blue-700">{formatCurrency(summary.total_credits || 0)}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Debits</p>
          <p className="text-lg font-bold text-red-700">{formatCurrency(summary.total_debits || 0)}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Account #" sortKey="account_number" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Client" sortKey="client_name" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                <SortableHeader label="Balance" sortKey="current_balance" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Credits" sortKey="total_credits" sortConfig={sortConfig} onSort={requestSort} align="right" />
                <SortableHeader label="Debits" sortKey="total_debits" sortConfig={sortConfig} onSort={requestSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No trust accounts found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.trust_account_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.account_number}</td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      onClick={() => navigate(`/trust-accounts/${item.trust_account_id}`)}
                      className="text-yellow-700 hover:text-yellow-900 hover:underline font-medium"
                    >
                      {item.client_name}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(item.current_balance)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.total_credits)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700">{formatCurrency(item.total_debits)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const colors = {
    active: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
    frozen: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

export default TrustBalanceReport;
